import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { jwtDecode } from 'jwt-decode';
import axios from 'axios';
import authService, { User, LoginCredentials, RegisterData } from '../services/auth.service';
import {
  isOidcConfigured,
  startLogin,
  completeLogin,
  trySilentLogin,
  clearTokens,
  logout as oidcLogout,
  CALLBACK_PATH,
} from '../services/authService';
import { getAccessToken, setAccessToken } from '../services/tokenStore';
import { refreshSession } from '../services/session';

interface DecodedToken {
  userId: string;
  email: string;
  role: string;
  exp: number;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  /** True when this deployment authenticates through Keycloak. */
  ssoEnabled: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  /** Redirect to Keycloak (authorization code + PKCE). */
  loginWithSso: (returnTo?: string) => Promise<void>;
  /** Finish the Keycloak round-trip; resolves to the path to return to. */
  completeSsoLogin: (params: URLSearchParams) => Promise<string>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => Promise<void>;
  updateUser: (user: User) => void;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // Read once per mount: env.js is static for the lifetime of the page.
  const ssoEnabled = isOidcConfigured();

  // Setup axios interceptors
  useEffect(() => {
    // Request interceptor - add token to all requests
    const requestInterceptor = axios.interceptors.request.use(
      (config) => {
        const accessToken = getAccessToken();
        if (accessToken) {
          config.headers.Authorization = `Bearer ${accessToken}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor - handle token refresh
    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // A 401 from the auth token endpoints themselves must NOT trigger
        // another refresh attempt. The refresh request travels through this
        // same interceptor, so retrying its own 401 recurses into an endless
        // storm of /auth/refresh calls (e.g. when the stored refresh token is
        // no longer valid). Let those reject so the caller can log the user out.
        const requestUrl: string = originalRequest?.url || '';
        const isAuthTokenEndpoint =
          requestUrl.includes('/auth/refresh') ||
          requestUrl.includes('/auth/login') ||
          requestUrl.includes('/auth/register');

        // If error is 401 and we haven't retried yet
        if (
          error.response?.status === 401 &&
          !originalRequest._retry &&
          !isAuthTokenEndpoint
        ) {
          originalRequest._retry = true;

          try {
            // SSO: Refresh bei Keycloak (/api/auth/refresh kennt nur den
            // lokalen Fluss und liegt im OIDC-Modus hinter oidcMiddleware).
            // Lokal: HttpOnly-Refresh-Cookie. Beides in refreshSession().
            const accessToken = await refreshSession();
            originalRequest.headers.Authorization = `Bearer ${accessToken}`;
            return axios(originalRequest);
          } catch (refreshError) {
            // Refresh failed, logout user
            clearTokens();
            setUser(null);
            if (ssoEnabled) {
              // Bounce through Keycloak: if the realm session is still alive
              // this is invisible to the user.
              startLogin().catch(() => {
                window.location.href = '/login';
              });
            } else {
              window.location.href = '/login';
            }
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );

    // Cleanup interceptors on unmount
    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, [ssoEnabled]);

  // Load user on mount
  useEffect(() => {
    const loadUser = async () => {
      try {
        // Tokens liegen nur im Speicher (services/tokenStore.ts): nach dem
        // Neuladen ist keines da und muss neu beschafft werden.
        const accessToken = getAccessToken();

        if (!accessToken) {
          if (ssoEnabled) {
            // Die Realm-Sitzung besteht meist schon -- die meisten kommen
            // ueber die Kachel im Comm WebUI, bereits angemeldet. Nicht auf
            // der Callback-Route, die gerade ihren eigenen Code einloest und
            // sonst um den PKCE-Verifier konkurrierte.
            if (window.location.pathname !== CALLBACK_PATH) {
              const recovered = await trySilentLogin().catch(() => false);
              const token = getAccessToken();
              if (recovered && token) {
                setUser(await authService.getCurrentUser(token));
              }
            }
            return;
          }

          // Lokaler Passwortfluss: das HttpOnly-Refresh-Cookie (falls
          // vorhanden) liefert ein neues Access-Token. Ohne Cookie 401 ->
          // nicht angemeldet, kein Fehler.
          try {
            const token = await refreshSession();
            setUser(await authService.getCurrentUser(token));
          } catch {
            clearTokens();
          }
          return;
        }

        // Token im Speicher (Provider neu eingehaengt): abgelaufen?
        const decoded = jwtDecode<DecodedToken>(accessToken);
        const currentTime = Date.now() / 1000;

        if (decoded.exp < currentTime) {
          try {
            const newAccessToken = await refreshSession();
            const userData = await authService.getCurrentUser(newAccessToken);
            setUser(userData);
          } catch {
            // Refresh failed
            clearTokens();
          }
        } else {
          // Token still valid, load user
          const userData = await authService.getCurrentUser(accessToken);
          setUser(userData);
        }
      } catch (error) {
        console.error('Error loading user:', error);
        clearTokens();
      } finally {
        setIsLoading(false);
      }
    };

    loadUser();
  }, [ssoEnabled]);

  const login = async (credentials: LoginCredentials) => {
    try {
      // Refresh-Token setzt der Server als HttpOnly-Cookie.
      const response = await authService.login(credentials);
      setAccessToken(response.accessToken);
      setUser(response.user);
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Login fehlgeschlagen');
    }
  };

  const loginWithSso = async (returnTo?: string) => {
    await startLogin(returnTo);
  };

  const completeSsoLogin = async (params: URLSearchParams) => {
    const returnTo = await completeLogin(params);
    const accessToken = getAccessToken() as string;
    // /api/auth/me is served from the JIT-provisioned row that
    // src/middleware/oidc.middleware.ts upserts from the token claims.
    const userData = await authService.getCurrentUser(accessToken);
    setUser(userData);
    return returnTo;
  };

  const register = async (data: RegisterData) => {
    try {
      const response = await authService.register(data);
      setAccessToken(response.accessToken);
      setUser(response.user);
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Registrierung fehlgeschlagen');
    }
  };

  const logout = async () => {
    if (ssoEnabled) {
      // Ends the Keycloak session too, otherwise the next login would be
      // an invisible re-login through the still-valid realm cookie.
      setUser(null);
      oidcLogout();
      return;
    }

    try {
      // Server entwertet das Refresh-Token aus dem Cookie und loescht es.
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      clearTokens();
      setUser(null);
    }
  };

  const updateUser = (updatedUser: User) => {
    setUser(updatedUser);
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    ssoEnabled,
    login,
    loginWithSso,
    completeSsoLogin,
    register,
    logout,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
